import { useState } from 'react';
import { Check, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * DragAndDrop component - Drag items to match categories
 * Data structure: { items: [{id, label, description}], categories: [{id, label, correct_items: [item_ids]}], feedback }
 */
export default function DragAndDrop({ simulationData, onComplete }) {
  const [draggedItem, setDraggedItem] = useState(null);
  const [itemPositions, setItemPositions] = useState({}); // { itemId: categoryId }
  const [showFeedback, setShowFeedback] = useState({}); // { itemId: 'correct' | 'incorrect' }

  const items = simulationData.items || [];
  const categories = simulationData.categories || [];
  const feedback = simulationData.feedback || {
    correct: 'Great! You matched it correctly!',
    incorrect: 'Not quite. Try again!',
    complete: 'Excellent! You\'ve matched all items correctly!'
  };

  const handleDragStart = (e, itemId) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, categoryId) => {
    e.preventDefault();
    if (!draggedItem) return;

    const newPositions = { ...itemPositions };
    newPositions[draggedItem] = categoryId;
    setItemPositions(newPositions);

    // Check if correct
    const category = categories.find(c => c.id === categoryId);
    const isCorrect = category?.correct_items?.includes(draggedItem);
    
    setShowFeedback({ ...showFeedback, [draggedItem]: isCorrect ? 'correct' : 'incorrect' });
    
    // Clear feedback after 2 seconds
    setTimeout(() => {
      setShowFeedback(prev => {
        const newFeedback = { ...prev };
        delete newFeedback[draggedItem];
        return newFeedback;
      });
    }, 2000);

    // Check if all items are correctly placed
    const allCorrect = items.every(item => {
      const categoryId = newPositions[item.id];
      const category = categories.find(c => c.id === categoryId);
      return category?.correct_items?.includes(item.id);
    });

    if (allCorrect && items.length > 0) {
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    }

    setDraggedItem(null);
  };

  const getItemCategory = (itemId) => itemPositions[itemId];
  const isItemCorrect = (itemId) => {
    const categoryId = itemPositions[itemId];
    if (!categoryId) return null;
    const category = categories.find(c => c.id === categoryId);
    return category?.correct_items?.includes(itemId);
  };

  const allCorrect = items.length > 0 && items.every(item => isItemCorrect(item.id) === true);

  return (
    <div className="space-y-6">
      {/* Items to drag */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Drag items to the correct category:
        </h3>
        <div className="flex flex-wrap gap-3">
          {items.map((item) => {
            const categoryId = getItemCategory(item.id);
            const isCorrect = isItemCorrect(item.id);
            const feedbackType = showFeedback[item.id];
            
            if (categoryId) return null; // Item already placed
            
            return (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item.id)}
                className={`cursor-move px-4 py-2 rounded-lg border-2 transition-all ${
                  feedbackType === 'correct'
                    ? 'bg-green-100 border-green-500 text-green-800'
                    : feedbackType === 'incorrect'
                    ? 'bg-red-100 border-red-500 text-red-800'
                    : 'bg-teal-100 dark:bg-teal-900/30 border-teal-400 text-teal-800 dark:text-teal-200 hover:bg-teal-200 dark:hover:bg-teal-900/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.description && (
                  <p className="text-xs mt-1 opacity-75">{item.description}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drop zones (Categories) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category) => {
          const categoryItems = items.filter(item => getItemCategory(item.id) === category.id);
          const allCategoryItemsCorrect = categoryItems.every(item => isItemCorrect(item.id) === true);
          
          return (
            <div
              key={category.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, category.id)}
              className={`min-h-[200px] p-4 rounded-lg border-2 border-dashed transition-all ${
                draggedItem
                  ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              <h4 className="font-bold text-lg mb-3 text-gray-900 dark:text-white">
                {category.label}
                {allCategoryItemsCorrect && categoryItems.length > 0 && (
                  <Check className="w-5 h-5 text-green-500 inline-block ml-2" />
                )}
              </h4>
              
              <div className="space-y-2">
                {categoryItems.map((item) => {
                  const isCorrect = isItemCorrect(item.id);
                  const feedbackType = showFeedback[item.id];
                  
                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded border-2 transition-all ${
                        isCorrect
                          ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-800 dark:text-green-200'
                          : feedbackType === 'incorrect'
                          ? 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-200'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{item.label}</span>
                        {isCorrect && <Check className="w-4 h-4 text-green-600" />}
                        {feedbackType === 'incorrect' && <X className="w-4 h-4 text-red-600" />}
                      </div>
                      {item.description && (
                        <p className="text-xs mt-1 opacity-75">{item.description}</p>
                      )}
                      {feedbackType && (
                        <p className={`text-xs mt-2 font-semibold ${
                          feedbackType === 'correct' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {feedbackType === 'correct' ? feedback.correct : feedback.incorrect}
                        </p>
                      )}
                    </div>
                  );
                })}
                
                {categoryItems.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-4">
                    Drop items here
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion message */}
      {allCorrect && (
        <div className="bg-green-100 dark:bg-green-900/30 border-2 border-green-500 rounded-lg p-4 text-center">
          <p className="text-green-800 dark:text-green-200 font-semibold">
            {feedback.complete || 'Excellent! You\'ve matched all items correctly!'}
          </p>
        </div>
      )}
    </div>
  );
}


